import React, { memo, ReactNode, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMediaPredicate } from "react-media-hook";
import ConfettiExplosion from "@reonomy/react-confetti-explosion";
import { FaTriangleExclamation } from "react-icons/fa6";

import CloseButton from "../../atoms/CloseButton/CloseButton";
import { ModalProps, ModalTypes } from "./Modal.types";
import Icon from "../../atoms/Icon/Icon";
import { IconValues } from "../../atoms/Icon/Icon.types";

import "./Modal.scss";

const Modal: React.FC<ModalProps> = ({
  show,
  handleClose,
  loading,
  children,
  showConfirm,
  confirmChildren,
  loadingChildren,
  title,
  type,
}) => {
  const isMobile = useMediaPredicate("(max-width: 630px)");

  useEffect(() => {
    if (show) {
      document?.body?.classList?.add("modal-open");
    } else {
      document?.body?.classList?.remove("modal-open");
    }
  }, [show]);

  useEffect(() => {
    return () => {
      document?.body?.classList?.remove("modal-open");
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div className="mp-organisms-modal--main">
          <Content
            mobile={isMobile}
            handleClose={handleClose}
            children={children}
            type={type}
            title={title}
            loading={loading}
            showConfirm={showConfirm}
            confirmChildren={confirmChildren}
            loadingChildren={loadingChildren}
          />
          {type === ModalTypes.celebrate && <Confetti />}

          <motion.div
            className="background"
            data-testid="modal-background"
            onClick={() => {
              if (loading) return;
              handleClose();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { delay: 0.25 } }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;

interface ContentProps {
  mobile: boolean;
  children: React.ReactNode;
  handleClose: () => void;
  type?: ModalTypes;
  title?: string;
  loading?: boolean;
  showConfirm?: boolean;
  confirmChildren?: React.ReactNode;
  loadingChildren?: React.ReactNode;
}

const Content = ({
  mobile,
  children,
  handleClose,
  type,
  title,
  loading,
  showConfirm,
  confirmChildren,
  loadingChildren,
}: ContentProps) => {
  return (
    <motion.div
      className={`content${mobile ? " mobile" : ""}`}
      initial={{
        opacity: 0,
        scale: mobile ? 1.0 : 0.7,
        y: mobile ? 40 : 0,
      }}
      animate={{
        opacity: 1,
        scale: 1.0,
        transition: { delay: 0.15 * (type === ModalTypes.celebrate ? 2 : 1) },
        y: 0,
      }}
      exit={{
        opacity: 0,
        scale: mobile ? 1.0 : 0.7,
        y: mobile ? 40 : 0,
      }}
    >
      <motion.div className={`header-container ${type ? type : ""}`}>
        {type === ModalTypes.warning && <FaTriangleExclamation />}
        {type === ModalTypes.celebrate && <Icon value={IconValues.celebrate} />}

        {title && <>{title}</>}
        {!loading && <CloseButton handleClose={handleClose} dark={false} />}
      </motion.div>

      <motion.div className="content-pad">
        {loading && (
          <FadeContainer>{loadingChildren && loadingChildren}</FadeContainer>
        )}
        {!loading && showConfirm && (
          <FadeContainer>{confirmChildren}</FadeContainer>
        )}
        {!loading && !showConfirm && <FadeContainer>{children}</FadeContainer>}
      </motion.div>
    </motion.div>
  );
};

interface FadeContainerProps {
  children: React.ReactNode;
}
const FadeContainer: React.FC<FadeContainerProps> = ({ children }) => {
  return (
    <motion.div
      className="fade-container"
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
        transition: { delay: 0.25 },
      }}
      exit={{
        opacity: 0,
      }}
    >
      {children}
    </motion.div>
  );
};

// const ConfettiWrapper = () => {
//   // const [x, setX] = useState(0);
//   // const [y, setY] = useState(0);
//   // const arr = Array.from(Array(count).keys());
//   return (
//     <div
//       className="confetti"
//       // onClick={(event) => {
//       //   console.log("event=", event.pageX);
//       // }}
//     >
//       <div style={{ transform: `translate(0px, 0px)` }}>
//         <Confetti />
//       </div>

//       {/* {arr.map((_value, index) => {
//         return <Confetti key={`confetti-${index}`} />;
//       })} */}
//     </div>
//   );
// };

const Confetti = memo(() => {
  const [isExploding, setIsExploding] = useState(false);
  useEffect(() => {
    setTimeout(() => setIsExploding(true), 400);
  }, []);
  return (
    <div className="confetti">
      {isExploding && (
        <ConfettiExplosion
          key={"confetti-1"}
          force={0.5}
          colors={["#fff", "#f27277", "#ffcb05", "#44c8f5"]}
          floorHeight={2000}
          floorWidth={4000}
        />
      )}
    </div>
  );
});

// const Confirmation = ({ close }) => {
//   return (
//     <motion.div
//       initial={{
//         opacity: 0,
//       }}
//       animate={{
//         opacity: 1,
//         transition: { delay: 0.25 },
//       }}
//     >
//       <motion.div>CONFIRM!</motion.div>
//       <motion.div>
//         <Button
//           label="ok"
//           type={ButtonTypes.ok}
//           size={ButtonSizes.small}
//           primary={false}
//           onClick={close}
//         />
//       </motion.div>
//     </motion.div>
//   );
// };
